import { Button, FormGroup, Switch } from '@mui/material';
import * as React from 'react';
import { useSegment } from 'common/hooks/segment';
import { PresetView } from 'pages/Shipments/Containers/types';
import { TableCellsIcon } from '@heroicons/react/24/solid';
import { DropdownButton } from './DropdownButton';

export interface ColumnProperties {
  label: string;
  key: string;
  visible: boolean;
  disabled?: boolean;
}

interface Props {
  columnStates: ColumnProperties[];
  setColumnStates: (columns: ColumnProperties[]) => void;
  presetView?: PresetView;
  usage: 'trackAndTrace' | 'shipments';
}

export const EditColumnsDropdown: React.FC<Props> = ({
  columnStates,
  setColumnStates,
  presetView,
  usage,
}) => {
  const { track } = useSegment();

  const handleOnChange = (c: ColumnProperties, visible: boolean) => {
    if (visible) {
      track(
        'Watchlist column added',
        { column: c.label, presetView, usage },
        { includeCompany: true, includeUser: true }
      );
    } else {
      track(
        'Watchlist column hidden',
        { column: c.label, presetView, usage },
        { includeCompany: true, includeUser: true }
      );
    }

    const newColumnStates = columnStates.map((columnProperty) => {
      if (c.key === columnProperty.key) {
        return {
          ...columnProperty,
          visible,
        };
      }
      return columnProperty;
    });
    setColumnStates(newColumnStates);
  };

  const setAll = (visible: boolean) => {
    if (visible) {
      track(
        'Watchlist column added',
        { column: 'all', presetView, usage },
        { includeCompany: true, includeUser: true }
      );
    } else {
      track(
        'Watchlist column hidden',
        { column: 'all', presetView, usage },
        { includeCompany: true, includeUser: true }
      );
    }

    const newColumnStates = columnStates.map((columnProperty) => {
      if (!columnProperty.disabled) {
        return {
          ...columnProperty,
          visible,
        };
      }
      return columnProperty;
    });
    setColumnStates(newColumnStates);
  };

  const hasHiddenColumns = columnStates.filter((c) => !c.visible).length > 0;
  const [hovered, setHovered] = React.useState(false);

  let iconColor = 'text-gray-500';
  if (hasHiddenColumns || hovered) {
    iconColor = 'text-blue';
  }

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <DropdownButton
        label={<TableCellsIcon className={`h-7 w-7 ${iconColor}`} />}
        variant="text"
        placement="bottom-end"
      >
        <h4 className="p-2 font-medium">Edit columns</h4>
        <FormGroup className="space-y-2 max-h-[300px] overflow-y-auto flex flex-col flex-nowrap">
          {columnStates.map((c) => (
            <Button
              key={c.key}
              onClick={() => {
                handleOnChange(c, !c.visible);
              }}
              startIcon={<Switch checked={c.visible} disabled={c.disabled} size="small" />}
              classes={{ text: 'text-dark', disabled: 'text-gray-400' }}
              style={{ justifyContent: 'flex-start' }}
            >
              {c.label}
            </Button>
          ))}
        </FormGroup>
        <div className="flex justify-between w-full mt-2">
          <Button onClick={() => setAll(false)}>Hide All</Button>
          <Button onClick={() => setAll(true)}>Show All</Button>
        </div>
      </DropdownButton>
    </div>
  );
};
EditColumnsDropdown.displayName = 'ContainerActions.EditColumnsDropdown';
